import {
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  Row,
  ColumnDef,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { DataLake } from '@finley-technologies/finley-types';

import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';

const getColumnDefs = (firstQueryDataItem: DataLake.AthenaQueryItemData) =>
  Object.keys(firstQueryDataItem).reduce<
    ColumnDef<DataLake.AthenaQueryItemData>[]
  >((acc, key) => {
    acc.push({
      header: key,
      // periods in keys are detected by TanStack as nested objects, so we define accessor functions instead of using an accessor key to prevent undefined object key errors
      accessorFn: (row) => row[key],
      cell: function displayCell({
        row,
      }: {
        row: Row<DataLake.AthenaQueryItemData>;
      }) {
        return key in row.original ? String(row.original[key]) : '';
      },
    });
    return acc;
  }, []);

const AthenaDataPreviewTable = ({
  queryDataItems,
}: {
  queryDataItems: DataLake.AthenaQueryItemData[];
}) => {
  const columnDefs = useMemo(() => getColumnDefs(queryDataItems?.[0] || []), [
    queryDataItems,
  ]);

  const table = useReactTable({
    data: queryDataItems,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const csvDownload = useMemo(
    () =>
      getTanstackVisibleDataForDownload({
        tableInstance: table,
      }),
    [table],
  );

  return (
    <>
      <CsvXlsxDownloadSelect
        dataRows={csvDownload.dataRows}
        headers={csvDownload.headers}
        filenamePrefix="athena-query-results"
      />
      <TableBETA table={table} />
      <TanStackPaginationControl table={table} />
    </>
  );
};

export const AthenaDataPreviewModal = (params: {
  queryDataItems: DataLake.AthenaQueryItemData[];
  onClose: () => void;
  message?: string;
}) => {
  const { queryDataItems, onClose, message } = params;

  return (
    <Modal
      id="athena-query-save-modal"
      isOpen={queryDataItems.length > 0}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent maxW="fit-content" maxH="4xl" p="2">
        <ModalHeader>
          <Text>
            Queried Data Results: {queryDataItems.length.toLocaleString()} Shown
            Items
          </Text>
          {message && (
            <Text mt="2" fontSize="md" fontWeight="normal">
              {message}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDirection="column" gap="1rem">
          <AthenaDataPreviewTable queryDataItems={queryDataItems} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
